import { UIIcon } from '../../../config/icons';
import { $trigger } from '../../y/events';
import { YFlags } from '../../y/types';
import { $el } from '../elements';
import { UIRelPos, UIValue } from '../types';
import { UIViewAddSpec, UIViewSpec } from '../view';
import { UIDropdown, UIDropdownViewGenerator } from './dropdown';
import { UIBaseInput, UIBaseInputOptions } from './input-base';
import { makeInputWrapper, UIAnyInputWrapper } from './input-wrapper';

export interface UISelectOptions<Key, Value> extends UIBaseInputOptions {
    generator?: (v: UIValue<Key>) => UIViewSpec;
    menu_kind?: string;
    keep_open?: boolean;
    no_focus?: boolean;
    searchable?: boolean;
    display?: (value: Value) => UIViewAddSpec | undefined;
    compact_title?: string;
}

abstract class UISelectBase<Key, Value> extends UIBaseInput<Value> {
    protected _values: UIValue<Key>[];
    protected _pending_values?: Promise<UIValue<Key>[]>;
    protected _pending_value?: Value;
    private _placeholder: string;
    private _menu_kind?: string;
    private _keep_open?: boolean;
    private _no_focus?: boolean;
    private _generator: UIDropdownViewGenerator<UIValue<Key>>;
    private _display: (value: Value) => UIViewAddSpec | undefined;
    private _search_input?: UIAnyInputWrapper;
    private _display_elem: HTMLElement;
    private _open_dropdown?: UIDropdown;
    private _compact_title: string;
    private _compact_mode?: boolean;

    constructor(values: UIValue<Key>[] | Promise<UIValue<Key>[]>, generator: UIDropdownViewGenerator<UIValue<Key>>, display: (value: Value) => (UIViewAddSpec | undefined), options: UISelectOptions<Key, Value> = {}) {
        options.r_deco = $el.ICON(UIIcon.ANGLE_DOWN, 'r-arrow');

        let input: HTMLElement = $el.DIV();
        let alt_input: HTMLElement | undefined;
        let search_input: UIAnyInputWrapper | undefined;

        if(options.searchable) {
            search_input = makeInputWrapper({});
            alt_input = input;
            input = search_input.element;
        }

        super(options, input, alt_input);
        this._search_input = search_input;
        this._display_elem = alt_input || input;
        this.classList.add('ui-select');
        input.tabIndex = 0;

        if(Array.isArray(values)) {
            this._values = values;
        } else {
            const promise = this._pending_values = values;
            this._values = [];
            values.then(values => {
                if(this._pending_values == promise)
                    this.values = values;
            });
        }

        let has_input_focus: boolean = false;
        let has_alt_focus: boolean = false;

        if(alt_input) {
            alt_input.tabIndex = 0;
            this.classList.add('f-alt-input');

            alt_input.addEventListener('focus', () => {
                has_alt_focus = true;
                if(!has_input_focus) {
                    this.classList.add('f-focus');
                    $trigger(this, 'focus');
                }
            });

            alt_input.addEventListener('blur', () => {
                has_alt_focus = false;
                if(!has_input_focus) {
                    this.classList.remove('f-focus');
                    $trigger(this, 'blur');
                }
            })
        }

        input.addEventListener('focus', () => {
            if(alt_input)
                this.classList.remove('f-alt-input');
            has_input_focus = true;
            if(!has_alt_focus) {
                this.classList.add('f-focus');
                $trigger(this, 'focus');
            }
        });

        input.addEventListener('blur', () => {
            if(alt_input)
                this.classList.add('f-alt-input');
            has_input_focus = false;
            if(!has_alt_focus) {
                this.classList.remove('f-focus');
                $trigger(this, 'blur');
            }

            if(this._open_dropdown) {
                this._open_dropdown.close();
                this._open_dropdown = undefined;
            }
        });

        this._placeholder = options.placeholder || '\u00a0';
        this._compact_title = options.compact_title || this._placeholder;
        this._menu_kind = options.menu_kind;
        this._keep_open = options.keep_open;
        this._display = display;
        this._generator = generator;
        this._no_focus = options.no_focus;

        this.root.addEventListener('click', () => { this.showDropdown(); });
    }

    showDropdown(where?: UIViewSpec) {
        if(this.disabled || this._pending_values) return;

        if(this._search_input) {
            this._search_input.text = '';
        }

        if(!this._no_focus) {
            this.classList.remove('f-alt-input');
            this.input_elem.focus();
        }

        const on_close = () => {
            this._open_dropdown = undefined;
            if(this._search_input) {
                this.classList.add('f-alt-input');
                this._display_elem.focus();
                this.input_elem.blur();
            }
        }

        const values = this._values.filter(v => !v.hidden);

        const menu = new UIDropdown(values, this._generator, { keep_open: this._keep_open, search: !this._search_input, search_input: this._search_input });
        this._open_dropdown = menu;

        menu.on_choose = (m, v) => {
            this.setDirty(true);
            this.doSelect(m.key, v);
        };

        if(this._menu_kind)
            menu.classList.add(this._menu_kind);
        if(this._menu_kind === 'k-mini')
            menu.open(where || this, { offset: -2, position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, match_size: true, adjust_size: true, can_flip: true, prevent_blur: true, on_close });
        else
            menu.open(where || this, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, match_size: true, adjust_size: true, can_flip: true, prevent_blur: true, on_close });
    }

    set values(values: UIValue<Key>[]) {
        this._pending_values = undefined;
        this._values = values;
        this.value = this._pending_value || this.value;
        this._pending_value = undefined;
    }

    get values() {
        return this._values;
    }

    set compact_mode(v: boolean) {
        this.classList.toggle('m-compact-mode', v);
        this._compact_mode = v;
        this.fixText();
    }

    get compact_mode() {
        return !!this._compact_mode;
    }

    protected fixText() {
        if(this._pending_values) {
            this._display_elem.textContent = '\u00a0';
            return;
        }

        let display = this._display(this.value);

        if(display == undefined) {
            this._display_elem.classList.add('f-placeholder');
            display = this._compact_mode ? this._compact_title : this._placeholder;
        } else {
            this._display_elem.classList.remove('f-placeholder');
        }

        this._display_elem.ui_empty(display || '\u00a0');
    }

    override focus() {
        this.classList.remove('f-alt-input');
        this.input_elem.focus();
    }

    protected doCheck() {
        this.setOkay(!this.required || !this.is_empty);
    }

    protected abstract doSelect(key: Key, view: UIViewSpec): void;
}

export class UISelect<Key> extends UISelectBase<Key, Key | undefined> {
    private _value: Key | undefined;

    constructor(values: UIValue<Key>[] | Promise<UIValue<Key>[]> = [], options: UISelectOptions<Key, Key | undefined> = {}) {
        const display = options.display || (value => {
            return this._values.find(v => v.key === value)?.name;
        })

        const view_generator = options.generator || (v => $el.SPAN('ui-dropdown-item', v.name));

        const generator = (v: UIValue<Key>) => {
            return { view: view_generator(v), search: v.name };
        }

        super(values, generator, display, options);
        this.value = undefined;
    }

    get value(): Key | undefined {
        return this._value;
    }

    set value(value: Key | undefined) {
        if(this._pending_values) {
            this._pending_value = value;
            this._value = value;
        } else {
            this._value = undefined;
            if(value !== undefined && this._values.some(v => v.key == value))
                this._value = value;
        }

        this.fixText();
        this.doCheck();
        this.fixDesign();

        $trigger(this, 'value', this._value);
    }

    get is_empty() {
        return this._value === undefined;
    }

    protected doSelect(key: Key) {
        this.value = key;
    }
}

export class UIMultiSelect<Key> extends UISelectBase<Key, Key[]> {
    private _value!: Key[];
    protected _is_checked: (value: UIValue<Key>) => boolean;

    constructor(values: UIValue<Key>[] | Promise<UIValue<Key>[]> = [], options: UISelectOptions<Key, Key[]> = {}) {
        const display = options.display || (value => {
            if(!value.length) return undefined;
            return this._values.filter(v => value.includes(v.key)).map(v => v.name).join(', ');
        })

        const view_generator = options.generator || (v => $el.SPAN('ui-dropdown-item m-multiple', [v.name, $el.ICON(UIIcon.CHECK, 'r-check')]));

        const generator = (v: UIValue<Key>) => {
            const span = view_generator(v);

            if(this._value.includes(v.key))
                span.classList.add('f-checked');

            return { view: span, search: v.name };
        }

        super(values, generator, display, options);
        this._is_checked = k => this._value.includes(k.key);
        this.value = [];
    }

    get value(): Key[] {
        return this._value;
    }

    set value(value: Key[]) {
        if(this._pending_values) {
            this._pending_value = value;
            this._value = value;
        } else {
            value = value || [];
            const values = this._values;
            this._value = value.filter(v => values.some(vv => vv.key == v));
        }

        this.fixText();
        this.doCheck();
        this.fixDesign();

        $trigger(this, 'value', this._value);
    }

    get is_empty() {
        return !this._value.length;
    }

    protected doSelect(key: Key, view: UIViewSpec) {
        if(this._value.includes(key)) {
            this.value = this._value.filter(k => k != key);
            view.classList.remove('f-checked');
        } else {
            this._value.push(key);
            this.value = this._value;
            view.classList.add('f-checked');
        }
    }
}

export class UIFlagsSelect<Key extends string> extends UISelectBase<Key, YFlags<Key>> {
    private _value!: YFlags<Key>;
    protected _is_checked: (value: UIValue<Key>) => boolean;

    constructor(values: UIValue<Key>[] | Promise<UIValue<Key>[]> = [], options: UISelectOptions<Key, YFlags<Key>> = {}) {
        const display = options.display || (value => {
            const values = this._values.filter(v => value[v.key]).map(v => v.name);
            if(!values.length) return undefined;
            return values.join(', ');
        })

        const view_generator = options.generator || (v => $el.SPAN('ui-dropdown-item m-multiple', [v.name, $el.ICON(UIIcon.CHECK, 'r-check')]));

        const generator = (v: UIValue<Key>) => {
            const span = view_generator(v);

            if(this._value[v.key])
                span.classList.add('f-checked');

            return { view: span, search: v.name };
        }

        super(values, generator, display, options);
        this._is_checked = k => !!this._value[k.key];
        this.value = {};
    }

    get value(): YFlags<Key> {
        return this._value;
    }

    set value(value: YFlags<Key>) {
        if(this._pending_values) {
            this._pending_value = value;
            this._value = value || {};
        } else {
            value = value || {};
            const values = this._values;
            this._value = Object.fromEntries(Object.entries(value).filter(([k, v]) => v && values.some(vv => vv.key == k))) as YFlags<Key>;
        }

        this.fixText();
        this.doCheck();
        this.fixDesign();

        $trigger(this, 'value', this._value);
    }

    get is_empty() {
        return Object.keys(this._value).length === 0;
    }

    protected doSelect(key: Key, view: UIViewSpec) {
        if(this._value[key]) {
            delete this._value[key];
            this.value = this._value;
            view.classList.remove('f-checked');
        } else {
            this._value[key] = true;
            this.value = this._value;
            view.classList.add('f-checked');
        }
    }
}
